<script setup>
import {computed, ref, watch, nextTick} from 'vue'
import camelCase from 'lodash/camelCase'
import {useItemDataStore} from '@/stores/itemData'
import {useViewStore} from '@/stores/view'

// Bespoke
import TableCellOdpCreditLogAmount from '@/bespoke/odp/TableCellOdpCreditLogAmount'

// Custom field components
import TableCellCustomersLogin from './TableCellCustomersLogin'
import TableCellEmailEventEvent from './TableCellEmailEventEvent'
import TableCellEmailMessageEvent from './TableCellEmailMessageEvent'
import TableCellEmailMessageTo from './TableCellEmailMessageTo'
import TableCellFilesFileName from './TableCellFilesFileName'
import TableCellFilesFileSize from './TableCellFilesFileSize'
import TableCellOrderItemsSku from './TableCellOrderItemsSku'
import TableCellOrderItemsTitle from './TableCellOrderItemsTitle'
import TableCellOrderItemsVariationId from './TableCellOrderItemsVariationId'
import TableCellOrderCustomerId from './TableCellOrderCustomerId'
import TableCellOrderLogs from './TableCellOrderLogs'
import TableCellOrderOrderStatus from './TableCellOrderOrderStatus'
import TableCellProductOptionsButtons from "./TableCellProductOptionsButtons"
import TableCellProductVariations from './TableCellProductVariations'
import TableCellProductTypesButtons from './TableCellProductTypesButtons'
import TableCellProductVariablesTitle from './TableCellProductVariablesTitle'
import TableCellUsersAssume from './TableCellUsersAssume'
import TableCellRedirectsSourceUrl from "./TableCellRedirectsSourceUrl"
import TableCellRedirectsTargetUrl from "./TableCellRedirectsTargetUrl"

// Column name components
import TableCellDisplayOrder from './TableCellDisplayOrder'

// Type components
import TableCellTypeCheckbox from "./type/TableCellTypeCheckbox"
import TableCellTypeCodeEditor from "./type/TableCellTypeCodeEditor"
import TableCellTypeColor from "./type/TableCellTypeColor"
import TableCellTypeCurrency from "./type/TableCellTypeCurrency"
import TableCellTypeDate from "./type/TableCellTypeDate"
import TableCellTypeDateDate from "./type/TableCellTypeDateDate"
import TableCellTypeDatetimeLocal from "./type/TableCellTypeDatetimeLocal"
import TableCellTypeEmail from "./type/TableCellTypeEmail"
import TableCellTypeFile from "./type/TableCellTypeFile"
import TableCellTypeImage from "./type/TableCellTypeImage"
import TableCellTypeInternalLink from "./type/TableCellTypeInternalLink"
import TableCellTypeJson from "./type/TableCellTypeJson"
import TableCellTypeNumber from "./type/TableCellTypeNumber"
import TableCellTypePassword from "./type/TableCellTypePassword"
import TableCellTypeRange from "./type/TableCellTypeRange"
import TableCellTypeRelationshipManyToMany from "./type/TableCellTypeRelationshipManyToMany"
import TableCellTypeRelationshipOneToMany from "./type/TableCellTypeRelationshipOneToMany"
import TableCellTypeSelect from "./type/TableCellTypeSelect"
import TableCellTypeTel from "./type/TableCellTypeTel"
import TableCellTypeText from "./type/TableCellTypeText"
import TableCellTypeTextarea from "./type/TableCellTypeTextarea"
import TableCellTypeTexteditor from "./type/TableCellTypeTexteditor"
import TableCellTypeTime from "./type/TableCellTypeTime"
import TableCellTypeUrl from "./type/TableCellTypeUrl"
import api from '@/services/api'

// Map components explicitly for local template resolution evaluation
const componentRegistry = {
    TableCellOdpCreditLogAmount,
    TableCellMRegistrantsRegistrantsLogin: TableCellCustomersLogin,
    TableCellEmailEventEvent,
    TableCellEmailMessageEvent,
    TableCellEmailMessageTo,
    TableCellFilesFileName,
    TableCellFilesFileSize,
    TableCellMBasketOrderitemsSku: TableCellOrderItemsSku,
    TableCellMBasketOrderitemsTitle: TableCellOrderItemsTitle,
    TableCellMBasketOrderitemsVariationId: TableCellOrderItemsVariationId,
    TableCellOrderCustomerId,
    TableCellOrderLogs,
    TableCellOrderOrderStatus,
    TableCellProductOptionsButtons,
    TableCellMProductsProductsVariations: TableCellProductVariations,
    TableCellProductTypesButtons,
    TableCellProductVariablesTitle,
    TableCellUsersAssume,
    TableCellRedirectsSourceUrl,
    TableCellRedirectsTargetUrl,
    TableCellDisplayOrder,
    TableCellTypeCheckbox,
    TableCellTypeCodeEditor,
    TableCellTypeColor,
    TableCellTypeCurrency,
    TableCellTypeDate,
    TableCellTypeDateDate,
    TableCellTypeDatetimeLocal,
    TableCellTypeEmail,
    TableCellTypeFile,
    TableCellTypeImage,
    TableCellTypeInternalLink,
    TableCellTypeJson,
    TableCellTypeNumber,
    TableCellTypePassword,
    TableCellTypeRange,
    TableCellTypeRelationshipManyToMany,
    TableCellTypeRelationshipOneToMany,
    TableCellTypeSelect,
    TableCellTypeTel,
    TableCellTypeText,
    TableCellTypeTextarea,
    TableCellTypeTexteditor,
    TableCellTypeTime,
    TableCellTypeUrl
}

const props = defineProps({
    id: Number,
    columnId: Number,
    viewId: String
})

// Dynamic View Store & Item Data Pinia Store
const viewStore = useViewStore(props.viewId)
const itemDataStore = useItemDataStore()

// Element references
const editableInput = ref(null)

// State flags
const editMode = ref(false)
const inlineEditValue = ref('')
const pattern = ref(false)
const errorText = ref('')
const nextElement = ref([])

// Async structures mapped to local variables
const column = ref(null)
const item = ref(null)

const tableName = computed(() => {
    return viewStore.tableName
})

const typeClass = computed(() => {
    return column.value ? 'table-cell-type-' + column.value.type : ''
})

const value = computed({
    get() {
        if (item.value && column.value) {
            return item.value[column.value.columnName]
        }
        return undefined
    },
    set(newValue) {
        if (item.value && column.value) {
            item.value[column.value.columnName] = newValue
        }
    }
})

const editable = computed(() => {
    return column.value?.browser === 2
})

const componentName = computed(() => {
    if (column.value) {
        let columnName = column.value.columnName

        // Custom field components
        let targetTableName = tableName.value.toLowerCase().replace(/_/g, '-')
        if (tableName.value === 'm_basket_orders') {
            targetTableName = 'order'
        }
        let compName = 'table-cell-' + targetTableName + '-' + columnName
        let camelcase = camelCase(compName)
        let vueComponentName = camelcase.charAt(0).toUpperCase() + camelcase.slice(1)

        if (vueComponentName in componentRegistry) {
            return componentRegistry[vueComponentName]
        }

        // Column name components
        vueComponentName = 'TableCell' + (columnName.charAt(0).toUpperCase() + columnName.slice(1))
        if (vueComponentName in componentRegistry) {
            return componentRegistry[vueComponentName]
        }

        // Type components
        let type = camelCase(column.value.type)
        vueComponentName = 'TableCellType' + (type.charAt(0).toUpperCase() + type.slice(1))
        if (vueComponentName in componentRegistry) {
            return componentRegistry[vueComponentName]
        }
    }
    return null
})

watch(() => props.columnId, async (newVal) => {
    if (!newVal) {
        column.value = null
        return
    }
    column.value = await itemDataStore.get({
        tableName: 'modulestructure',
        id: newVal
    })
}, {immediate: true})

watch([tableName, () => props.id], async ([newTable, newId]) => {
    if (!newTable || !newId) {
        item.value = null
        return
    }
    item.value = await itemDataStore.get({
        tableName: newTable,
        id: newId
    })
}, {immediate: true})

// Methods
const edit = (e) => {
    if (editable.value) {
        e.preventDefault()
        e.stopPropagation()

        setNextElement(e)
        editMode.value = true

        if (column.value.type === 'currency') {
            pattern.value = '[0-9]+(\\.[0-9][0-9]?)?'
            errorText.value = 'Invalid price.'
        }

        inlineEditValue.value = value.value

        nextTick(() => {
            if (editableInput.value) {
                editableInput.value.select()
            }
        })
    }
}

const saveEditable = async () => {
    if (!editMode.value) return

    if (editableInput.value?.checkValidity()) {
        value.value = inlineEditValue.value

        await itemDataStore.set({
            tableName: tableName.value,
            id: props.id,
            property: column.value.columnName,
            value: value.value
        })

        try {
            await api.patch(`api/component/${tableName.value}/${props.id}`, {
                [column.value.columnName]: value.value
            })
        } catch (err) {
            // Handle error if needed
        }

        setTimeout(() => {
            editMode.value = false
        }, 100)
    } else if (errorText.value) {
        console.error('Needs to display validation error')
    }
}

const setNextElement = (e) => {

}

const tabToNext = () => {

}
</script>

<template>
    <td v-if="column && item" :class="[typeClass, { 'editable': editable && !editMode, 'editing': editMode }]">

        <input v-if="editMode" ref="editableInput" v-model="inlineEditValue" :pattern="pattern"
               class="editableInput form-control" @blur="saveEditable" @click.stop=""
               @keydown.tab="tabToNext" @keyup.enter="saveEditable" @keyup.esc.prevent="editMode = false"
               @keyup.tab.stop.prevent="">

        <span v-else-if="editable" class="editable-container" @mousedown="edit">
            <component :is="componentName" :column="column" :item="item" :value="value" :view-id="viewId"/>
        </span>

        <component v-else :is="componentName" :column="column" :item="item" :value="value" :view-id="viewId"/>
    </td>

    <td v-else-if="column && item && !componentName">
        <span v-html="value"/>
    </td>
</template>

<style scoped>
.editing {
    position: relative;
}

.editable > span {
    padding: 10px 0;
    cursor: pointer;
}

.editable > span > span {
    cursor: pointer;
    color: #103a7c;
    border-bottom: 1px dashed #ccc;
}

.editable > span:hover > span {
    border-bottom-color: #666;
}

.editableInput {
    position: absolute;
    width: calc(100% - 1rem);
    font-size: inherit;
    text-align: inherit;
    border: 1px solid #ccc;
    padding: 0 3px;
}
</style>